import { comSessao } from "@/lib/sessaoServidor";
import { useEffect, useState } from "react";
import type { GetServerSideProps, NextPage } from "next";
import { signIn, getProviders } from "next-auth/react";
import Router, { useRouter } from "next/router";
import Link from "next/link";
import LayoutBranco from "../../components/layouts/LayoutBlanco";
import FormUsuario from "@/components/usuarios/FormUsuario";
import ApiHelper from "@/shared/ApiHelper";
import { Alert, Button, Label, TextInput } from "flowbite-react";
import AuthCard from "@/components/layouts/AuthCard";
import InputSenha from "@/components/Forms/InputSenha";
import { GoogleReCaptcha, useGoogleReCaptcha } from "react-google-recaptcha-v3";
import analytics from "@/lib/analytics";

interface IProps {
  searchParam?: { [key: string]: string | string[] | undefined };
  providers?: any;
  tipo?: string;
  rota?: string;
  backgroundImage?: string;
  clientIp?: any;
}

const getServerSidePropsSemSessao: GetServerSideProps = async (context) => {
  const clientIp =
    context.req.headers["x-forwarded-for"] || context.req.socket.remoteAddress;
  const images = [
    "/images/festa_bg_blur5.jpg",
    "/images/festa_bg_blur7.jpg",
    "/images/festa_bg_blur9.jpg",
    "/images/festa_bg_blur11.jpg",
  ];
  const randomIndex = Math.floor(Math.random() * images.length);
  const randomImage = images[randomIndex];

  let tipo = null;
  const rota = "r" in context.query ? context.query.r : null;

  if (context.query.tipo == "login") {
    tipo = "Login";
  }

  if (context.query.tipo == "register") {
    tipo = "Register";
  }

  return {
    props: {
      providers: await getProviders(),
      tipo,
      rota,
      backgroundImage: randomImage,
      clientIp,
    },
  };
};

export default function Auth({
  tipo,
  searchParam,
  rota,
  backgroundImage,
  clientIp,
}: IProps) {
  const router = useRouter();
  const [authType, setAuthType] = useState(tipo);
  const [error, setError] = useState("");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [spinner, setSpinner] = useState(false);
  const [termos, setTermos] = useState(false);

  const [isValidRecaptcha, setValidRecaptcha] = useState(true);
  const [refreshReCaptcha, setRefreshReCaptcha] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();

  useEffect(() => {
    getApiRecaptcha();
  },[]);

  const getApiRecaptcha = async (token?: string) => {
    
    // Token real do reCAPTCHA v3 (vem do <GoogleReCaptcha onVerify>)
    
    if (!token) return null;
  
    try {
      const res = await ApiHelper.post({
        method: "POST",
        url: ApiHelper.recaptcha,
        data: { recaptchaToken: token },
      });
        
      if (res.response === true) {
        setValidRecaptcha(true);
        setRefreshReCaptcha(false)
      }

      return res;

    } catch (error) {
      setValidRecaptcha(false);
      setRefreshReCaptcha(true)
    }

    return null;
  };

  const handleRecaptchaChange = async (value: any) => {
    await getApiRecaptcha(value)
  };

  const redirectToHome = () => {
    const { pathname } = Router;

    if (pathname === "/auth/[tipo]") {
      if (rota == "cart") {
        window.location.href = "/checkout/cart";
      } else {
        //window.open("/")
        window.location.href = "/";
      }
    } else {
      console.log("nada");
    }
  };

  const loginUser = async () => {
    
    // const resp = await getApiRecaptcha()
    // if (resp === null) {
    //     toastCustom.show({
    //         mensagem: 'É necessário responder aos desafios do recaptcha. Reinicie a página!',
    //         type: 'warning'
    //     })
    //     return
    // }

    setSpinner(true);
    // reCAPTCHA v3 (transparente): o backend confere a ação "login"
    const recaptchaToken = executeRecaptcha
      ? await executeRecaptcha("login").catch(() => "")
      : "";
    const res: any = await signIn("credentials", {
      redirect: false,
      email: email,
      password: password,
      recaptchaToken,
      callbackUrl: `/`,
    });

    setSpinner(false);
    if (res) {
      if (res.error) {
        setError(res.error);
      } else {
        await analytics.login();
        redirectToHome();
      }
    }
  };

  const formSubmit = async (event: any) => {
    event.preventDefault();

    setError("");
    loginUser();
  };

  const mudarTipo = () => {
    authType === "Login" ? setAuthType("Register") : setAuthType("Login");
  };

  const onChangeCheckBox = (e: {
    target: { checked: boolean; value: React.SetStateAction<string> };
  }) => {
    setTermos(() => e.target.checked);
  };

  if (authType == "Register") {
    return (
      <AuthCard titulo="Crie sua conta">
        <FormUsuario
          isPageADM={false}
          alterarTipo={(authType) => {
            setAuthType(authType);
          }}
        />
        <p className="text-center text-sm text-gray-600 mt-4">
          Já possui uma conta?{" "}
          <Link href="/auth/login" onClick={() => setAuthType("Login")} className="font-medium text-myred">
            Entrar ›
          </Link>
        </p>
      </AuthCard>
    );
  }

  return (
    <AuthCard titulo="Faça login em sua conta" ajuda={true}>
      <form onSubmit={formSubmit} className="space-y-5">
        {searchParam?.message && <p>{searchParam?.message}</p>}
        {error != "" && (
          <Alert color="failure">
            <span>
              <span className="font-bold">Atenção!</span>
              {" " + error}
            </span>
          </Alert>
        )}
        <div>
          <div className="mb-2 block">
            <Label htmlFor="email" value="E-mail" />
          </div>
          <TextInput
            id="email"
            type="text"
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Digite seu e-mail"
            autoComplete="username"
            required={true}
            autoFocus={true}
          />
        </div>
        <div>
          <div className="mb-2 flex items-center justify-between">
            <Label htmlFor="senha" value="Senha" />
            <Link href="/auth/recuperar" className="text-sm font-medium text-myred">
              Esqueceu sua senha? ›
            </Link>
          </div>
          <InputSenha
            id="senha"
            value={password}
            onChange={setPassword}
            placeholder="Digite sua senha"
            autoComplete="current-password"
          />
        </div>
        <Button
          type="submit"
          color={spinner ? "gray" : "failure"}
          className="w-full"
        >
          {spinner ? "AGUARDE.." : "ENTRAR"}
        </Button>
        <p className="text-center text-sm text-gray-600">
          Não tem uma conta?{" "}
          <button
            type="button"
            onClick={() => setAuthType("Register")}
            className="font-medium text-myred"
          >
            Cadastre-se ›
          </button>
        </p>
        {isValidRecaptcha === false && (
          <div className="flex justify-center">
            <GoogleReCaptcha onVerify={handleRecaptchaChange} refreshReCaptcha={refreshReCaptcha} />
          </div>
        )}
      </form>
    </AuthCard>
  );
}

Auth.layout = LayoutBranco;

export const getServerSideProps = comSessao(getServerSidePropsSemSessao);
